import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HyperLinkRedirectControlPatientProfileSubTabs } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { IInvitationContactEmail, IInvitationContactPhone } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/ContactForm/AddContactForm/AddContactForm";
import { InvitationForm, InvitationFormChangeEvent, InvitationFormHandle, InvitationFormValid } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/InvitationForm";
import { InvitationPopupProvider } from "@/Common/Components/Portal/InvitationPopup/InvitationPopupProvider";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { sessionStore } from "@/Globals/Stores/SessionStore";
// eslint-disable-next-line import/no-restricted-paths
import { RelationsHelper } from "@/Patient/Helpers/RelationsHelper";
import { InformationLoadingComponent } from "../../Loader/InformationLoadingComponent";
import useAccountDetails from "./Hooks/useAccountDetails";
import { LockedAccountAlert } from "./LockedAccountAlert/LockedAccountAlert";
import { InvitationPopupFooter } from "./InvitationPopupFooter";

interface InvitationPopupProps {
    person: Person | null;
    onCancel?: () => void;
    onRedirect?: () => void;
    skipLockedStatusNote?: boolean;
}

function InvitationPopup(props: Readonly<InvitationPopupProps>): JSX.Element {
    const { person, onCancel, onRedirect, skipLockedStatusNote } = props;

    const formRef = useRef<InvitationFormHandle>(null);

    const { getText } = useTranslation();
    const [resending, setResending] = useState<boolean>(false);
    const [unlocking, setUnlocking] = useState<boolean>(false);
    const [createOwn, setCreateOwn] = useState<boolean>(false);
    const [refusing, setRefusing] = useState<boolean>(false);

    const [validator, setValidator] = useState<InvitationFormValid>({
        isValidToSend: false,
        isValidToInvite: false
    });

    const contacts = useMemo<{ emails: IInvitationContactEmail[], phones: IInvitationContactPhone[] }>(
        () => {
            if (!person) {
                return { emails: [], phones: [] };
            }

            const emails: IInvitationContactEmail[] = person?.Emails.filter(p => p.NotificationsEnabled).map(email => ({
                address: email.Address,
                contactPointId: email.ContactPointTypeLookupId
            }));

            const phones: IInvitationContactPhone[] = person?.Phones.filter(p => p.NotificationsEnabled).map(phone => ({
                countryLookupId: phone.CountryLookupId,
                contactPointId: phone.ContactPointTypeLookupId,
                phoneTypeId: phone.PhoneTypeLookupId,
                number: phone.Number
            }));

            return { emails, phones };
        },
        [person]
    );

    const representatives =
        (person.Relationships || [])
            .filter(relation =>
                relation.IsRepresentative
                && !!relation.Individual.PortalSyncState
                && relation.Individual.PortalSyncState !== PortalRegistrationState.Default
                && relation.Individual.PortalSyncState !== PortalRegistrationState.PortalRefused
            )
            .map(relation => ({
                contactId: relation.Individual.Contact_id,
                permissions: RelationsHelper.getPermissionsArray(relation.PermissionFlags)
            }));

    const anyRepresentative =
        (person.Relationships || [])
            .some(relation => relation.IsRepresentative);

    const hasAccount = useMemo<boolean>(
        () =>
            !!person.PortalSyncState
            && person.PortalSyncState !== PortalRegistrationState.Default
            && person.PortalSyncState !== PortalRegistrationState.PortalRefused
            && person.PortalSyncState !== PortalRegistrationState.ChildAccount,
        [person.PortalSyncState]
    );

    const underAgeOfConsent = useMemo<{ dateOfBirth: Date, check: boolean }>(
        () => {
            const dateOfBirth = person.DateOfBirth;
            let check = false;

            if (dateOfBirth === null || dateOfBirth === undefined)
                return { dateOfBirth, check };

            check = (new Date()).getFullYear() - dateOfBirth.getFullYear() <= cacheStore.ClinicSettings.PatientAgeOfConsent;

            return { dateOfBirth, check };
        },
        [person]
    );

    const { details, loading } = useAccountDetails(person.ContactId, {
        accountStatus: person.PortalSyncState,
        enabled: hasAccount || person.PortalSyncState === PortalRegistrationState.PortalRefused
    });

    const accountStatusLocked = useMemo<boolean>(
        () => (person.PortalSyncState === PortalRegistrationState.InvitationLocked) || (details.status === PortalRegistrationState.InvitationLocked),
        [person.PortalSyncState, details.status]
    );

    const accountStatusRefused = person.PortalSyncState === PortalRegistrationState.PortalRefused || details.status === PortalRegistrationState.PortalRefused;

    const accountStatusActive = person.PortalSyncState === PortalRegistrationState.RegistrationComplete || details.status === PortalRegistrationState.RegistrationComplete;

    const title = useMemo<string>(
        () => refusing
            ? getText("PortalInvitation_AccountCreationRefusalLabel")
            : (hasAccount && !resending && !accountStatusLocked)
                ? getText("PortalAccountStatusLabel")
                : getText("PortalRegistrationLabel"),
        [hasAccount, resending, accountStatusLocked, refusing, getText]
    );

    const footer = (
        <InvitationPopupFooter
            patientContactId={person.ContactId}
            patientContactFacetId={person.ContactFacetId}
            patientPortalSyncState={person.PortalSyncState}
            accountStatusLocked={accountStatusLocked}
            unlocking={unlocking}
            refusing={refusing}
            resending={resending}
            createOwn={createOwn}
            hasAccount={hasAccount}
            accountStatusRefused={accountStatusRefused}
            accountStatusActive={accountStatusActive}
            representatives={representatives}
            setUnlocking={setUnlocking}
            setRefusing={setRefusing}
            onCancel={onCancel}
            validator={validator}
            formRef={formRef}
        />
    );

    const validate = useCallback(
        ({ validator }: InvitationFormChangeEvent) => {
            setValidator(validator);
        },
        []
    );

    const redirect = onRedirect ?? (
        (): void => {
            sessionStore.dispatch(LocalOperations.Misc.RedirectToTab, {
                currentLocation: RouterHistory.location,
                value: person.ContactId,
                target: RedirectTo.Patient,
                navigateToSubTab: new HyperLinkRedirectControlPatientProfileSubTabs(ProfileTab.Relationships)
            });

            onCancel?.();
        }
    );

    useEffect(() => {
        if (skipLockedStatusNote && accountStatusLocked) {
            setUnlocking(true);
        }
    }, [skipLockedStatusNote, accountStatusLocked]);

    const renderInvitationPopupBody = (): JSX.Element => {
        const mandatoryFields = {
            firstName: person.FirstName || null,
            lastName: person.LastName || null,
            sexAtBirthLookupId: person.GenderLookupId || null,
            dateOfBirth: person.DateOfBirth || null
        };

        if (refusing) {
            return (
                <>
                    <InvitationForm
                        ref={formRef}
                        hidden={refusing}
                        details={details}
                        fields={mandatoryFields}
                        emails={contacts.emails}
                        phones={contacts.phones}
                        onChange={validate}
                    />

                    <p>{getText("PortalInvitation_ConfirmRefuseAccountLabel")}</p>
                </>
            );
        }
        else if (accountStatusLocked && (!unlocking && !skipLockedStatusNote)) {
            return <LockedAccountAlert isAlertPopup={!unlocking} />;
        }
        else if (accountStatusLocked && (unlocking || skipLockedStatusNote)) {
            return (
                <>
                    <LockedAccountAlert isAlertPopup={!unlocking} />

                    <InvitationForm
                        ref={formRef}
                        hidden={refusing}
                        details={details}
                        fields={mandatoryFields}
                        emails={contacts.emails}
                        phones={contacts.phones}
                        onChange={validate}
                    />
                </>
            );
        }
        else {
            return (
                <InvitationForm
                    ref={formRef}
                    hidden={refusing}
                    details={details}
                    fields={mandatoryFields}
                    emails={contacts.emails}
                    phones={contacts.phones}
                    onChange={validate}
                />
            );
        }
    };

    return (
        <InvitationPopupProvider
            redirect={redirect}
            resending={resending}
            setResending={setResending}
            createOwn={createOwn}
            setCreateOwn={setCreateOwn}
            hasAccount={hasAccount}
            unlocking={unlocking}
            accountStatus={person.PortalSyncState}
            underAgeOfConsent={underAgeOfConsent}
            representatives={representatives}
            anyRepresentative={anyRepresentative}
        >
            <Modal
                width={600}
                onClose={onCancel}
                draggable={false}
                title={title}
                footer={footer}
                bodyStyle={{ maxHeight: "calc(100vh - 131px)" }}
                overlayZIndex={43}
                fixedOverlayPosition
                disableOverlayClose
                centerContent
                showOverlay
                showX
            >
                {loading
                    ? (
                        <InformationLoadingComponent
                            smallVersion={true}
                            customMessage={cacheStore.GetString("WaitForPortalLabel")}
                        />
                    )
                    : renderInvitationPopupBody()
                }
            </Modal>
        </InvitationPopupProvider>
    );
}

export { InvitationPopup };
